import { useState } from "react";

import { useTranslation } from "react-i18next";

import {
	useBulkAddToTrackerMutation,
	useBulkAssignMutation,
	useBulkChangeStatusMutation,
} from "@/pages/Private/redux/list/list.api";
import { useBulkBlacklistMutation } from "@/pages/Private/redux/list/list-bulk-status.api";
import { listSelector, removeFromInbox } from "@/pages/Private/redux/list/list.slice";
import { useTrackerDestinationLists } from "@/pages/Private/pages/List/utils/useTrackerDestinationLists";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	AssignStatus,
	InclusionFilterStatus,
	InclusionStatus,
	PriorityFilterStatus,
	SelectType,
} from "@/enum/list.enum";

import { User } from "@/pages/Public/pages/Login/schema/login";

import {
	useCreateBlacklistMutation,
	useUnsubscribeLeadMutation,
} from "@/pages/Private/redux/blacklist/blacklist.api";
import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import {
	companyBlacklistEntry,
	selectionOfLeads,
} from "@/components/CompanyBlacklistToggle/companyBlacklist";
import { CompanyBlacklistToggle } from "@/components/CompanyBlacklistToggle/CompanyBlacklistToggle";

import { useSearch } from "@/utils";
import { useSingleFlight } from "@/utils/useSingleFlight";
import { runOrToast } from "@/utils/runOrToast";

import { IFilterWithValues } from "../components/FilterModal";
import { TrackerDestinationPicker } from "../components/TrackerDestinationPicker/TrackerDestinationPicker";
import { LemlistAutoCompleteItem } from "../components/LemlistAutoComplete/LemlistAutoComplete";
import { LemlistResponse, ListPerson } from "../schema/list";

import { assertBulkAddSucceeded } from "./bulkAddResult";
import { useTrackerAddHint } from "./useTrackerAddHint";

export const useBulkAssign = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`inbox.${key}`);
	const dispatch = useAppDispatch();
	const { inbox: data } = useAppSelector(listSelector);

	const { setSearchValue: setTrackerSearchValue, query: trackerQuery } = useSearch();

	const {
		lists: combinedTrackerAndJobChangeLists,
		isLoading: isLoadingTrackerLists,
		isFetching: isFetchingTrackerLists,
	} = useTrackerDestinationLists({ searchValue: trackerQuery });

	const [bulkAssign, { isLoading: bulkReassignIsLoading }] = useBulkAssignMutation();
	const [selectedTracker, setSelectedTracker] = useState<AutoCompleteItem | null>();
	const [bulkAddToTracker, { isLoading: bulkAddToTrackerIsLoading }] =
		useBulkAddToTrackerMutation();
	const [bulkChangeStatus, { isLoading: bulkChangeStatusIsLoading }] =
		useBulkChangeStatusMutation();
	const [addEntireCompanyToBlacklist, setAddEntireCompanyToBlacklist] = useState<boolean>(false);
	const [createBlacklist, { isLoading: isCreatingBlacklist }] = useCreateBlacklistMutation();
	const [unsubscribeLead, { isLoading: isUnsubscribing }] = useUnsubscribeLeadMutation();
	const [bulkBlacklist, { isLoading: isBlacklisting }] = useBulkBlacklistMutation();
	const createBlacklistIsLoading = isCreatingBlacklist || isUnsubscribing || isBlacklisting;
	const [showBlacklistModal, setShowBlacklistModal] = useState<boolean>(false);
	// A double click must not write the entries twice (PREC-4425).
	const blacklistConfirm = useSingleFlight();
	const [showAddToTrackerModal, setShowAddToTrackerModal] = useState<boolean>(false);
	// A double click on "Add" must not send the add twice.
	const trackerAddConfirm = useSingleFlight();
	// Why Add has nothing to send, instead of a silent no-op (PREC-4497).
	const trackerAddHint = useTrackerAddHint();

	/**
	 * Records a lead the user unsubscribes (PREC-2548): the same record the lead's own unsubscribe
	 * link writes, which an override may not remove. The whole company, when asked for, is a user
	 * entry: the company did not unsubscribe.
	 */
	const unsubscribeSelectedLead = async (lead: ListPerson) => {
		await unsubscribeLead({ listPersonId: lead.id, listId: lead.list.id }).unwrap();

		const company = addEntireCompanyToBlacklist
			? companyBlacklistEntry(lead.prospect?.organization)
			: null;

		if (company) {
			await createBlacklist(company).unwrap();
		}
	};

	const handleBulkAddToBlacklist = async (
		selectedItemForConfirmation: ListPerson,
		asUnsubscribe = false
	) => {
		if (asUnsubscribe) {
			await unsubscribeSelectedLead(selectedItemForConfirmation);
		} else {
			// The server writes the lead's entry, then its company's, then moves it (PREC-4435).
			await bulkBlacklist({
				listId: selectedItemForConfirmation.list.id,
				assign: [{ listPersonId: selectedItemForConfirmation.id }],
				selectType: SelectType.CURRENT_PAGE,
				addCompany: addEntireCompanyToBlacklist,
			}).unwrap();
		}

		dispatch(removeFromInbox(selectedItemForConfirmation.id));

		setAddEntireCompanyToBlacklist(false);
		setShowBlacklistModal(false);
	};

	/**
	 * Adds the lead to the picked tracker and answers whether it worked. A failure is shown to the
	 * user and keeps the picker open (PREC-4497): it used to close it exactly like a success. With
	 * no tracker picked or no lead to add it sends nothing and says so.
	 */
	const handleAddToTracker = async ({
		selectedItems,
	}: {
		selectedItems: { listPersonId: number }[];
	}): Promise<boolean> => {
		if (!selectedTracker?.id) {
			return trackerAddHint.refuse("pickTracker");
		}

		if (selectedItems.length === 0) {
			return trackerAddHint.refuse("selectLead");
		}

		const request = {
			listId: +selectedTracker.id,
			listPersonIds: selectedItems.map((item) => item.listPersonId),
			selectType: SelectType.CURRENT_PAGE,
		};

		return runOrToast(
			async () => assertBulkAddSucceeded(await bulkAddToTracker(request).unwrap()),
			t("inbox.trackerFooter.addFailed")
		);
	};

	const renderAddToTrackerModal = ({
		selectedItems,
	}: {
		selectedItems: { listPersonId: number }[];
	}) => {
		return (
			<Modal
				handleClose={() => {
					setSelectedTracker(null);
					trackerAddHint.clear();
					setShowAddToTrackerModal(false);
				}}
				handleSave={() =>
					trackerAddConfirm.run(async () => {
						if (!(await handleAddToTracker({ selectedItems }))) {
							return;
						}

						setSelectedTracker(null);
						setShowAddToTrackerModal(false);
					})
				}
				isLoading={bulkAddToTrackerIsLoading || trackerAddConfirm.isRunning}
				isOpened={showAddToTrackerModal}
				size="md"
				submitButtonText={"Add"}
				title={"Add Person to Custom Signal Tracker"}
			>
				<TrackerDestinationPicker
					hint={trackerAddHint.hint}
					isFetching={isFetchingTrackerLists}
					isLoading={isLoadingTrackerLists}
					lists={combinedTrackerAndJobChangeLists}
					selectedTracker={selectedTracker}
					onSearch={setTrackerSearchValue}
					onSelect={(tracker) => {
						trackerAddHint.clear();
						setSelectedTracker(tracker);
					}}
				/>
			</Modal>
		);
	};

	const handleBulkStatusChange = async ({
		id,
		selectedItems,
		assignStatus,
		priorityFilterStatus,
		inclusionStatus,
	}: {
		id: number;
		selectedItems: { listPersonId: number }[];
		assignStatus?: AssignStatus;
		priorityFilterStatus: PriorityFilterStatus[];
		inclusionStatus?: InclusionStatus;
	}) => {
		if (!data) {
			return;
		}

		await bulkChangeStatus({
			listId: id ? +id : 0,
			assign: selectedItems,
			assignStatus: assignStatus ?? undefined,
			selectType: SelectType.CURRENT_PAGE,
			inclusionStatus: inclusionStatus ?? undefined,
			priorityFilterStatus: priorityFilterStatus ?? undefined,
		}).unwrap();

		if (assignStatus === AssignStatus.DONE || assignStatus === AssignStatus.ARCHIVED) {
			selectedItems.forEach((item) => {
				dispatch(removeFromInbox(item.listPersonId));
			});
		}
	};

	const handleBulkReassignInternalCampaigns = async ({
		id,
		selectedItems,
		assignedUser,
		filter,
		inclusionStatus,
	}: {
		id: number;
		selectedItems: { listPersonId: number; campaignId: string }[];
		assignedUser?: User;
		filter: IFilterWithValues;
		inclusionStatus: InclusionFilterStatus;
	}) => {
		if (!data) {
			return;
		}

		const param = selectedItems
			.map(({ listPersonId, campaignId }) => {
				return {
					listPersonId,
					campaignId,
					assignedToUserId: assignedUser?.id,
				};
			})
			.filter(Boolean) as Array<{
			listPersonId: number;
			campaignId?: string;
			assignedToUserId?: number;
		}>;

		await bulkAssign({
			listId: id,
			assign: param.filter(Boolean) as {
				listPersonId: number;
				campaignId?: string;
				listId?: number;
				assignedToUserId?: number;
			}[],
			assignStatus: AssignStatus.ASSIGNING,
			filter: filter,
			selectType: SelectType.CURRENT_PAGE,
			inclusionFilterStatus: inclusionStatus ? [inclusionStatus] : undefined,
		}).unwrap();
	};

	const handleBulkReassign = async (
		id: number,
		selectedItems: number[],
		lemlists: LemlistResponse,
		selectedCampaign: LemlistAutoCompleteItem,
		selectedInternalCampaign: LemlistAutoCompleteItem,
		selectedItemForConfirmation: ListPerson,
		assignedUser: User,
		filter: IFilterWithValues,
		selectType: SelectType,
		inclusionStatus: InclusionFilterStatus
	) => {
		if (!data) {
			return;
		}

		let param: Array<{
			listPersonId: number;
			lemlistId?: number;
			campaignId?: string;
			listId?: number;
			assignedToUserId?: number;
		}> = [];

		const lemlist = lemlists?.data.find((c) => c.id === selectedCampaign?.id);
		const internalCampaign = selectedInternalCampaign?.id;

		if (!lemlist && !internalCampaign) {
			return;
		}

		if (selectedItemForConfirmation) {
			const cObj = data.find((item) => item.id === selectedItemForConfirmation.id);

			if (!cObj) {
				return;
			}

			const lemlistId = lemlist?.id;

			if (!lemlistId && !internalCampaign) {
				return;
			}

			if (cObj) {
				param = [
					{
						listPersonId: selectedItemForConfirmation.id,
						lemlistId: lemlistId,
						campaignId: internalCampaign as string,
						assignedToUserId: assignedUser?.id,
					},
				];
			}
		} else {
			param = selectedItems
				.map((id) => {
					const cObj = data.find((item) => item.id === id);

					if (!cObj) {
						return;
					}

					const lemlistId = lemlist?.id;

					if (!lemlistId && !internalCampaign) {
						return;
					}

					return {
						listPersonId: id,
						lemlistId: lemlistId,
						campaignId: internalCampaign as string,
						assignedToUserId: assignedUser?.id,
					};
				})
				.filter(Boolean) as Array<{
				listPersonId: number;
				lemlistId?: number;
				campaignId?: string;
				assignedToUserId?: number;
			}>;
		}

		await bulkAssign({
			listId: id,
			assign: param.filter(Boolean) as {
				listPersonId: number;
				lemlistId?: number;
				campaignId?: string;
				listId?: number;
				assignedToUserId?: number;
			}[],
			assignStatus: AssignStatus.ASSIGNING,
			filter: filter,
			selectType: selectType,
			inclusionFilterStatus: inclusionStatus ? [inclusionStatus] : undefined,
		}).unwrap();
	};

	const renderBlacklistModal = ({
		selectedItemForConfirmation,
		asUnsubscribe = false,
	}: {
		selectedItemForConfirmation: ListPerson;
		/** The Inbox's "Unsubscribe": record an unsubscribe rather than a user blacklist entry. */
		asUnsubscribe?: boolean;
	}) => {
		if (!selectedItemForConfirmation) {
			return null;
		}

		return (
			<Modal
				handleClose={() => setShowBlacklistModal(false)}
				handleSave={() =>
					blacklistConfirm.run(async () => {
						await handleBulkAddToBlacklist(selectedItemForConfirmation, asUnsubscribe);
						setShowBlacklistModal(false);
					})
				}
				isLoading={blacklistConfirm.isRunning}
				isOpened={showBlacklistModal}
				size="xs"
				submitButtonColor={ButtonColor.DANGER}
				submitButtonText={ts(asUnsubscribe ? "unsubscribeLead" : "toBlackList")}
				title={ts(asUnsubscribe ? "unsubscribeLeadTitle" : "addingToBlacklist")}
			>
				{ts(
					asUnsubscribe
						? "unsubscribeLeadConfirmationMessage"
						: "addingToBlacklistConfirmationMessage"
				)}

				<CompanyBlacklistToggle
					isChecked={addEntireCompanyToBlacklist}
					selection={selectionOfLeads([selectedItemForConfirmation])}
					onChange={setAddEntireCompanyToBlacklist}
				/>
			</Modal>
		);
	};

	return {
		handleBulkReassign,
		handleBulkReassignInternalCampaigns,
		bulkReassignIsLoading,
		handleBulkStatusChange,
		bulkChangeStatusIsLoading,
		renderBlacklistModal,
		handleBulkAddToBlacklist,
		showBlacklistModal,
		setShowBlacklistModal,
		createBlacklistIsLoading,
		renderAddToTrackerModal,
		setShowAddToTrackerModal,
		bulkAddToTrackerIsLoading,
	};
};
